import { useEffect, useRef, useState } from "react";

interface UsePreloadedImageSrcParams {
  url: string | undefined;
  /**
   * When this value changes, the new url is swapped in immediately without
   * waiting for a background preload. Useful for user-driven changes such as
   * carousel navigation, where a delayed swap would feel laggy.
   */
  immediateKey?: unknown;
  /**
   * Invoked when the background preload (or the rendered `<img>`) fails to
   * load. The latest reference is captured automatically, so callers do not
   * need to memoize this callback.
   */
  onError?: (failedUrl: string) => void;
}

interface DisplayedState {
  url: string;
  key: unknown;
}

/**
 * Returns a "displayed" url that mirrors `url` after preloading the new image
 * in the background, preventing the visible flash that otherwise occurs when a
 * `src` swap forces the browser to fetch the new image synchronously (common
 * with rotating signed URLs).
 *
 * The very first url is rendered without preload (there is nothing to flash
 * from). When `immediateKey` changes, the new url is also swapped in
 * immediately - this is meant for user-driven changes like clicking a
 * carousel arrow, where waiting for a network round-trip would feel laggy.
 */
export const usePreloadedImageSrc = ({
  url,
  immediateKey,
  onError,
}: UsePreloadedImageSrcParams): string | undefined => {
  const [displayed, setDisplayed] = useState<DisplayedState | undefined>(() =>
    url ? { url, key: immediateKey } : undefined,
  );

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const displayedRef = useRef(displayed);
  displayedRef.current = displayed;

  useEffect(() => {
    if (!url) {
      if (displayedRef.current !== undefined) {
        setDisplayed(undefined);
      }
      return;
    }

    const current = displayedRef.current;

    if (!current || current.key !== immediateKey) {
      setDisplayed({ url, key: immediateKey });
      return;
    }

    if (current.url === url) {
      return;
    }

    let cancelled = false;
    const img = new window.Image();
    img.src = url;
    img.onload = () => {
      if (!cancelled) {
        setDisplayed({ url, key: immediateKey });
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        onErrorRef.current?.(url);
      }
    };

    return () => {
      cancelled = true;
    };
  }, [url, immediateKey]);

  return displayed?.url;
};
